import type {ListItemBase} from "@t/list/List";
import {DataGetter} from "@t/fd/layout/Agenda.ts";
import {CellFormatter, CellFormatterData} from "@t/agenda/CellFormatter.ts";

export type ColType = string;

export interface ColTypeMap {
    [key:string]: ColType;
}
export const ColumnType:ColTypeMap = {
	SELECT: "select",		// column with row selection checkbox
	DELETE: "delete",		// column with row delete icon
	SORT: "sort",			// column with a sorting handle

    STRING: 'S',
    HTML: 'R',  //previous H - changed to compatible with dyn.attr html types
    TEXT: 'Q',
    DATE: 'D',
    DATETIME: '*',
    DATE_AND_TIME: '!',
    TIME: '?',
    INT: 'I',
    LONG: 'V',
    FLOAT: 'F',
    BOOLEAN: 'B',
    SET: 'T',
    TRINITY: '3',
    USER: 'U',
    WORD: 'W', //alows {=,<>,in, not in}
	LINK: '€',

	// References to other row objects
	PARTY: 'C',
    OPPORTUNITY: 'O',
    OFFER: 'E',
    BUSCASE: 'K',
    BCITEM: 'X', // deprecated
    TASK: 'Y',
    EVENT: 'Z',
    PROBLEM: 'G',
    REGION: 'N',
	CAMPAIGN: 'J',
	PRODUCT: 'A', //Article

    MULTIPLE: 'M',
    EADDRESS: '@',
    FULLTEXT: '_', //general key - int which can be null instead 0
    KEY: '^', //general key - int which can be null instead 0
    KEY_MULTI: ':', //general multiple key - value,value
    LIST: 'L'		// BusCaseTypeItem
};


export type LayoutMode =
	"NORMAL"    // standard layout mode (normal layouting based on budget)
	| "LEFT"	// stick to the left (spans all runs)
	| "RIGHT"	// stick to the right (spans all runs)
	| "TOP"		// column is spanned over all data columns (excl. sticking columns) and kept at the top
	| "BOTTOM"	// column is spanned over all data columns (excl. sticking columns) and kept at the bottom
	;

// com.anneca.intouch.agenda.ColumnLayout
export interface ColumnLayout {
	name: string;
	type: ColType;

	/** Defines stickiness of the column to sides of a row */
	layout?: LayoutMode;
	/** The column will be rendered with fixed with. The value must be specified in CSS units. It should be specified without horizontal padding of table cells (net width).  */
	fixedWidth?: string;
}

/**
 * Sometimes it is useful to attach formatting information to a column
 * in the view. This object allows to associate a column name with
 * styling and formatting info.
 */
export interface ColumnStyle {
	/** Horizontal alignment of the text */
	justify?: "LEFT" | "RIGHT" | "CENTER";
	/** Vertical alignment of the content. Default vertical alignment is baseline */
	verticalAlign?: "TOP" | "BASELINE" | "MIDDLE" | "BOTTOM";
	/** Font size modifier */
	size?: "SMALL" | "NORMAL" | "LARGE";
	/** Font weight modifier */
	weight?: "THIN" | "NORMAL" | "THICK" | "BOLD";
	/** ID of EventColor to tint foreground color */
	fg?: number;
	/** ID of EventColor to tint background color */
	bg?: number;
}

export const USER_LIST_NAME = "com.anneca.intouch.list.UsersList";
export const GROUP_LIST_NAME = "com.anneca.intouch.list.GroupsList";
export const USER_GROUP_LIST_NAME = "com.anneca.intouch.list.UsersGroupsList";

export interface Column extends ColumnLayout, ColumnStyle {
	/** Unique name of the column (typically DB column name) */
    name: string;
	/** Column alias or null if name should be used */
	alias?: string;
	/** Table name if the column is in a table */
	tableName?: string;
	/** Table alias if the column is in a table */
	tableAlias?: string;
	/** Full DB name contains alias prefix, e.g. "table.total" */
	fullName?: string;

	/** Column data type descriptor */
	type: ColType;
	/** Human-readable name*/
    title: string;
	/** Human-readable name (shorter, used for column heading) */
    thitle?: string;
	/** Icon which can be used in table header. To create header with only-icon, set this property and thitle="" */
	icon?: string;
    /** Property propName in Java bean can differ from db propName */
    property?: string;
	/** Reference to associated list */
    listName?: string;
	/** Can be used for sorting tables */
	canSort?: boolean;
	/** Can be used to filter data */
    canFilter?: boolean;
	/** Can be summarized (for example calculated max, min, average, ...) */
	summary?: boolean;
	/** This column can be presented in agenda grid view */
	showInAgenda?: boolean;
	/** This column can be presented in agenda view in UI but has no DB data */
    fakeColumn?: boolean;
	/** Data inside this column can be in-place edited */
    editable?: boolean;
	/** Query-by-example filtering supported (displays widget in TH) */
    qbeEnable?: boolean;
	/** Human-readable description of this column */
	description?: string;
	/** The column has meaning for folder-row */
    folderColumn?: boolean;
	/** The column can be used in projection part of a SELECT command */
    useInSelect?: boolean;
	/** Can be used in plugin for special rights, not supported at standard CRM */
    sensitive?: boolean;
	/** This column can be vieved only with user with required role right */
    requiredRole?: string;
	/** TODO meaning of this property is unclear, SHOULD BE REMOVED */
    listListener?: string;

	/* How to obtain required data from JSON */
	/* These are rules how to retrieve variables $0 ..$N that can later be formatted to display table cell */
	/* If the getter is missing default strategy for obtaining data is used (based on column name/property name) */
	jsonGetters?: DataGetter[];
	pojoGetters?: object;

	/* Specifies how to format retrieved data to cell's html content */
	format?: CellFormatterData;
	_formatter?: CellFormatter;		// used to cache resolved format implementation (doesn't come from the server, it's calculated)
}

function nameAliasMatch(name:string|null, targetName:string|null|undefined, targetAlias:string|null|undefined):boolean {
	if (name == null) return false;
	if (targetAlias == null) {
		if (targetName == null) return false;
		return targetName.toLowerCase() === name.toLowerCase();
	} else {
		return targetAlias.toLowerCase() === name.toLowerCase();
	}
}

/**
 * Tests if the given Column has specified name
 */
export function hasThisName(col:Column, name:string):boolean {
	if (col == null || name == null) return false;

	let justColName:string;
	let justTableName:string;
	const dot = name.indexOf('.');
	if (dot > 0) {
		justColName = name.substring(dot + 1);
		justTableName = name.substring(0, dot);
	} else {
		justColName = name;
		justTableName = null;
	}

	const tabMatch: boolean = justTableName == null || nameAliasMatch(justTableName, col.tableName, col.tableAlias);
	const nameMatch:boolean = nameAliasMatch(justColName, col.name, col.alias);

	return tabMatch && nameMatch;
}

/**
 * See ColumnOption.toJson()
 */
export interface ColumnListItem extends ListItemBase, ColumnStyle {
    type: ColType;
	list?: string;
	primaryKey?: boolean;
	/** This column cannot be used for filtering (filter editting) */
	noFilter?: boolean;
	/** Cannot be shown in agenda */
	noView?: boolean;
	/** Cannot be sorted by */
	noSort?: boolean;
}

